import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Columns,
  Trash2,
  X,
} from 'lucide-react';
import { TableSkeleton } from './LoadingSkeleton';
import EmptyState from './EmptyState';

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

const BULK_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'on-hold', label: 'On Hold' },
];

interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  pageSize?: number;
  total?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  keyField?: string;
  emptyMessage?: string;
  // #2 — Bulk actions
  onBulkDelete?: (ids: string[]) => void;
  onBulkStatusChange?: (ids: string[], status: string) => void;
  // #3 — Column visibility persistence key
  tableId?: string;
  // #5 — Error state
  error?: boolean;
  onRetry?: () => void;
}

function useColumnVisibility(tableId: string | undefined, columns: { key: string; label: string }[]) {
  const storageKey = tableId ? `datatable_cols_${tableId}` : null;

  const [hidden, setHidden] = useState<Set<string>>(() => {
    if (!storageKey) return new Set<string>();
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return new Set<string>(JSON.parse(stored));
    } catch {
      // ignore
    }
    return new Set<string>();
  });

  const toggleColumn = useCallback(
    (key: string) => {
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          // Prevent hiding the last visible column
          const visibleCount = columns.length - next.size;
          if (visibleCount <= 1) return prev;
          next.add(key);
        }
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify([...next]));
          } catch {
            // ignore
          }
        }
        return next;
      });
    },
    [columns.length, storageKey],
  );

  return { hidden, toggleColumn };
}

export default function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  onRowClick,
  pageSize = 20,
  total,
  page = 1,
  onPageChange,
  keyField = 'id',
  emptyMessage = 'No records found',
  onBulkDelete,
  onBulkStatusChange,
  tableId,
  error = false,
  onRetry,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // #2 — Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>('');

  // #3 — Column visibility
  const colDefs = columns.map((c) => ({ key: String(c.key), label: c.label }));
  const { hidden: hiddenCols, toggleColumn } = useColumnVisibility(tableId, colDefs);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef<HTMLDivElement>(null);

  // #7 — Jump-to-page
  const [jumpValue, setJumpValue] = useState('');

  const totalPages = total ? Math.ceil(total / pageSize) : undefined;

  // Close column menu on outside click
  useEffect(() => {
    if (!colMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) {
        setColMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colMenuOpen]);

  // Reset selection when data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [data]);

  if (loading) return <TableSkeleton rows={pageSize > 10 ? 8 : pageSize} />;

  // #5 — Error state
  if (error) {
    return (
      <div className="card overflow-hidden">
        <EmptyState
          variant="error"
          action={
            onRetry ? (
              <button onClick={onRetry} className="btn-primary text-sm px-4 py-2">
                Retry
              </button>
            ) : undefined
          }
        />
      </div>
    );
  }

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = sortKey
    ? [...data].sort((a, b) => {
        const av = a[sortKey as keyof T];
        const bv = b[sortKey as keyof T];
        const cmp = String(av || '').localeCompare(String(bv || ''), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : data;

  const visibleColumns = columns.filter((c) => !hiddenCols.has(String(c.key)));

  // Bulk helpers
  const allIds = sorted.map((row) => String(row[keyField as keyof T] ?? ''));
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && allIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;
  const selectedArray = [...selectedIds];

  const handleBulkDelete = () => {
    onBulkDelete?.(selectedArray);
    setSelectedIds(new Set());
  };

  const handleBulkStatusChange = (status: string) => {
    if (!status) return;
    onBulkStatusChange?.(selectedArray, status);
    setBulkStatus('');
    setSelectedIds(new Set());
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setBulkStatus('');
  };

  // #7 — Jump-to-page handlers
  const commitJump = () => {
    if (!totalPages || !onPageChange) return;
    const parsed = parseInt(jumpValue, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.max(1, Math.min(parsed, totalPages));
      onPageChange(clamped);
    }
    setJumpValue('');
  };

  const handleJumpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commitJump();
  };

  const hasBulkActions = !!(onBulkDelete || onBulkStatusChange);

  return (
    <div className="card overflow-hidden relative">
      {/* #3 — Column visibility toolbar */}
      <div className="flex items-center justify-end px-4 pt-3 pb-1">
        <div className="relative" ref={colMenuRef}>
          <button
            onClick={() => setColMenuOpen((v) => !v)}
            className="btn-ghost flex items-center gap-1.5 text-xs text-text-muted hover:text-text px-2 py-1.5"
            aria-label="Toggle column visibility"
          >
            <Columns size={13} />
            Columns
          </button>
          {colMenuOpen && (
            <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-card border border-border rounded-xl shadow-lg py-1.5">
              {columns.map((col) => {
                const key = String(col.key);
                const isHidden = hiddenCols.has(key);
                return (
                  <label
                    key={key}
                    className="flex items-center gap-2.5 px-3 py-1.5 text-sm text-text hover:bg-bg/60 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={!isHidden}
                      onChange={() => toggleColumn(key)}
                      className="accent-primary w-3.5 h-3.5"
                    />
                    {col.label}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg/50">
              {/* #2 — Select-all checkbox */}
              {hasBulkActions && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleSelectAll}
                    className="accent-primary w-3.5 h-3.5 cursor-pointer"
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {visibleColumns.map((col) => (
                <th
                  key={String(col.key)}
                  className={`px-4 py-3 text-left font-semibold text-xs text-text-muted uppercase tracking-wide whitespace-nowrap ${col.width || ''} ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}`}
                >
                  {col.sortable ? (
                    <button
                      onClick={() => handleSort(String(col.key))}
                      className="flex items-center gap-1 hover:text-text transition-colors"
                    >
                      {col.label}
                      {sortKey === String(col.key) ? (
                        sortDir === 'asc' ? (
                          <ChevronUp size={12} />
                        ) : (
                          <ChevronDown size={12} />
                        )
                      ) : (
                        <ChevronsUpDown size={12} className="text-text-dim" />
                      )}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (hasBulkActions ? 1 : 0)}>
                  <EmptyState description={emptyMessage} />
                </td>
              </tr>
            ) : (
              sorted.map((row, i) => {
                const rowId = String(row[keyField as keyof T] ?? i);
                const isSelected = selectedIds.has(rowId);
                return (
                  <tr
                    key={rowId}
                    onClick={() => onRowClick?.(row)}
                    className={`border-b border-border/50 last:border-0 transition-colors hover:bg-bg/70 ${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'bg-primary/5' : ''}`}
                  >
                    {/* #2 — Row checkbox */}
                    {hasBulkActions && (
                      <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectRow(rowId)}
                          className="accent-primary w-3.5 h-3.5 cursor-pointer"
                          aria-label={`Select row ${rowId}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={String(col.key)}
                        className={`px-4 py-3 text-text ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}`}
                      >
                        {col.render ? col.render(row) : String(row[col.key as keyof T] ?? '—')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — #7 jump-to-page included */}
      {totalPages && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-bg/30">
          <p className="text-xs text-text-muted">
            Page {page} of {totalPages} · {total} total
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange?.(page - 1)}
              disabled={page <= 1}
              className="btn-ghost p-1 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>

            {/* #7 — Jump-to-page */}
            <div className="flex items-center gap-1.5 text-xs text-text-muted">
              <span>Go to page:</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={jumpValue}
                onChange={(e) => setJumpValue(e.target.value)}
                onKeyDown={handleJumpKeyDown}
                onBlur={commitJump}
                className="w-12 px-1.5 py-0.5 rounded border border-border bg-bg text-text text-xs text-center focus:outline-none focus:border-primary"
                placeholder={String(page)}
                aria-label="Jump to page"
              />
            </div>

            <button
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= totalPages}
              className="btn-ghost p-1 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* #2 — Floating bulk-action bar */}
      {hasBulkActions && selectedCount > 0 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-card border border-border shadow-xl text-sm whitespace-nowrap">
          <span className="font-semibold text-text">
            {selectedCount} selected
          </span>

          {onBulkStatusChange && (
            <select
              value={bulkStatus}
              onChange={(e) => handleBulkStatusChange(e.target.value)}
              className="text-xs bg-bg border border-border rounded-lg px-2 py-1 text-text focus:outline-none focus:border-primary cursor-pointer"
              aria-label="Change status for selected rows"
            >
              <option value="">Change Status</option>
              {BULK_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}

          {onBulkDelete && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors text-xs font-medium"
              aria-label="Delete selected rows"
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}

          <button
            onClick={handleClearSelection}
            className="flex items-center gap-1 text-text-muted hover:text-text transition-colors"
            aria-label="Clear selection"
          >
            <X size={14} />
            <span className="text-xs">Clear</span>
          </button>
        </div>
      )}
    </div>
  );
}
